import React from 'react';
import { FileCode, X, ChevronRight } from 'lucide-react';
import { highlightSync } from '../utils/shiki-highlighter';
import highlightCSharp from '../utils/highlight-csharp';
import { useCursorBlink } from '../hooks/use-cursor-blink';

/**
 * Video variant of CodeEditor — uses Remotion hooks (always called unconditionally).
 * Used inside Remotion scenes only.
 */
// Editor visible area height (approx lines fitting in viewport)
const LINE_HEIGHT = 21;
const EDITOR_PADDING = 12;
const VISIBLE_LINES = 22; // ~462px editor height at 1080p

const CHAR_WIDTH = 8.4; // Cascadia Code at 14px
const GUTTER_PADDING = 16; // pl-4

// Build SVG wavy underline data URI for a given hex color
function wavyUnderline(color) {
  const encoded = encodeURIComponent(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='6' height='3'%3E%3Cpath d='M0 3 Q1.5 0 3 3 Q4.5 6 6 3' fill='none' stroke='${encoded}' stroke-width='1'/%3E%3C/svg%3E")`;
}

const SEVERITY_COLOR = { error: '#F44747', warning: '#CCA700', info: '#4EC9B0' };

export default function CodeEditorVideo({
  typedCode = '',
  step = 1,
  lineCount,
  projectName = 'HelloWorld',
  filename = 'Program.cs',
  highlightLines,
  highlightOpacity = 0.2,
  errors,
  scrollToLine,
  breakpoints,
  debugLine,
  tabs,
}) {
  const cursorOpacity = useCursorBlink();
  const lines = lineCount || (typedCode ? typedCode.split('\n').length : 1);
  const highlighted = highlightSync(typedCode) || highlightCSharp(typedCode);

  // Auto-scroll: keep cursor line visible with 3 lines bottom margin
  const targetLine = scrollToLine || lines;
  const scrollOffset = Math.max(0, (targetLine - VISIBLE_LINES + 3) * LINE_HEIGHT);

  return (
    <div className="flex flex-col flex-1 overflow-hidden min-w-0">
      {/* Tab bar */}
      <div className="flex h-[35px] bg-[#2d2d30] border-b border-[#3f3f41] flex-shrink-0">
        {(tabs || [{ name: filename, active: true }]).map((tab) => (
          <div key={tab.name} className={`flex items-center gap-1.5 px-3 text-[12px] min-w-[130px] relative ${
            tab.active ? 'bg-[#1e1e1e] text-[#ccc] border-t-2 border-[#007ACC]' : 'bg-[#2d2d30] text-[#858585] border-t-2 border-transparent'
          }`}>
            <FileCode size={13} className={tab.active ? 'text-[#4EC9B0]' : 'text-[#6a6a6a]'}/>
            <span>{tab.name}</span>
            {tab.active && step === 1 && <span className="w-2 h-2 rounded-full bg-[#ccc] ml-auto"/>}
            <X size={11} className="text-[#555] hover:text-[#ccc] hover:bg-white/10 rounded ml-1 p-[1px] cursor-pointer"/>
          </div>
        ))}
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center h-[22px] bg-[#1e1e1e] border-b border-[#2d2d30] px-3 gap-1 text-[11px] text-[#858585] flex-shrink-0">
        <span>{projectName}</span>
        <ChevronRight size={11}/>
        <span className="text-[#4EC9B0]">{filename}</span>
        <ChevronRight size={11}/>
        <span className="text-[#DCDCAA]">{step >= 2 ? 'Main(string[])' : 'Program'}</span>
      </div>

      {/* Code editor pane */}
      <div className="flex flex-1 overflow-hidden bg-[#1e1e1e]" style={{ fontFamily: "'Cascadia Code','Consolas','Courier New',monospace", fontSize: 14, lineHeight: `${LINE_HEIGHT}px` }}>
       <div className="flex flex-1" style={{ transform: `translateY(-${scrollOffset}px)`, transition: 'transform 0.15s ease-out' }}>
        {/* Line numbers gutter */}
        <div className="select-none text-right pr-4 pl-4 py-3 text-[#858585] border-r border-[#2a2a2a] flex-shrink-0 min-w-[52px]" style={{ fontSize: 14, lineHeight: `${LINE_HEIGHT}px` }}>
          {Array.from({ length: Math.max(lines, 14) }, (_, i) => {
            const lineNum = i + 1;
            const isDebug = lineNum === debugLine;
            const isBp = breakpoints?.includes(lineNum);
            return (
              <div key={i} className={lineNum === lines ? 'text-[#ccc]' : ''} style={{ position: 'relative' }}>
                {isBp && (
                  <div style={{
                    position: 'absolute', left: -12, width: 14, height: 14,
                    borderRadius: '50%', background: '#E51400',
                    top: '50%', transform: 'translateY(-50%)',
                  }}/>
                )}
                {isDebug
                  ? <span style={{ color: '#FFD700', fontWeight: 'bold' }}>▶</span>
                  : lineNum
                }
              </div>
            );
          })}
        </div>

        {/* Code folding column */}
        <div className="select-none py-3 text-[#454545] text-[10px] flex-shrink-0 w-4" style={{ lineHeight: '21px' }}>
          {(() => {
            // Compute fold lines dynamically from lines containing '{'
            const foldLines = typedCode ? typedCode.split('\n').reduce((acc, line, i) => {
              if (line.includes('{')) acc.push(i + 1);
              return acc;
            }, []) : [];
            const totalLines = Math.max(lines, 14);
            return Array.from({ length: totalLines }, (_, i) => {
              const lineNum = i + 1;
              return foldLines.includes(lineNum)
                ? <div key={lineNum} className="text-center">⌄</div>
                : <div key={lineNum}/>;
            });
          })()}
        </div>

        {/* Code content */}
        <div className="flex-1 py-3 pl-4 overflow-visible whitespace-pre relative">
          {/* Current line highlight */}
          <div className="absolute left-0 right-0 bg-[#282828] pointer-events-none"
               style={{ height: 21, top: 12 + (lines - 1) * 21, borderLeft: '2px solid #007ACC' }}/>

          {/* Highlight lines (for highlight scene) */}
          {highlightLines && highlightLines.map(ln => (
            <div key={ln} className="absolute left-0 right-0 pointer-events-none"
                 style={{ height: 21, top: 12 + (ln - 1) * 21, background: `rgba(255, 255, 0, ${highlightOpacity})` }}/>
          ))}

          {/* Breakpoint line tints */}
          {breakpoints && breakpoints.map(ln => (
            <div key={`bp-${ln}`} className="absolute left-0 right-0 pointer-events-none"
                 style={{ height: LINE_HEIGHT, top: EDITOR_PADDING + (ln - 1) * LINE_HEIGHT, background: 'rgba(229, 20, 0, 0.15)' }}/>
          ))}

          {/* Debug line tint */}
          {debugLine && (
            <div className="absolute left-0 right-0 pointer-events-none"
                 style={{ height: LINE_HEIGHT, top: EDITOR_PADDING + (debugLine - 1) * LINE_HEIGHT, background: 'rgba(255, 255, 0, 0.1)' }}/>
          )}

          {/* Error squiggly lines (column-range) */}
          {errors && errors.map((err, idx) => {
            const color = SEVERITY_COLOR[err.severity] || SEVERITY_COLOR.error;
            const left = (err.startCol - 1) * CHAR_WIDTH + GUTTER_PADDING;
            const width = (err.endCol - err.startCol) * CHAR_WIDTH;
            return (
              <div key={idx} className="absolute pointer-events-none"
                   style={{
                     height: 3,
                     top: EDITOR_PADDING + (err.line - 1) * LINE_HEIGHT + LINE_HEIGHT - 3,
                     left,
                     width,
                     backgroundImage: wavyUnderline(color),
                     backgroundRepeat: 'repeat-x',
                     backgroundPosition: 'bottom',
                     backgroundSize: '6px 3px',
                   }}/>
            );
          })}

          <span dangerouslySetInnerHTML={{ __html: highlighted }}
                style={{ position: 'relative', zIndex: 1 }}/>

          {/* Blinking cursor */}
          {step === 1 && (
            <span style={{
              display: 'inline-block', width: 2, height: 16,
              background: '#aeafad', verticalAlign: 'text-bottom',
              opacity: cursorOpacity, position: 'relative', zIndex: 1,
            }}/>
          )}
        </div>

       </div>{/* close scroll wrapper */}

        {/* Minimap */}
        <div className="w-[90px] flex-shrink-0 bg-[#1a1a1a] border-l border-[#2a2a2a] overflow-hidden relative">
          <div className="absolute top-2 left-0 right-0 h-12 bg-white/5 border border-white/8 pointer-events-none"
               style={{ top: 8 + Math.min(scrollOffset * 0.17, 80) }}/>
          <div className="p-1 opacity-50 pointer-events-none"
               style={{ transform: `scale(0.17) translateY(-${scrollOffset}px)`, transformOrigin: 'top left', whiteSpace: 'pre',
                        fontFamily: 'Consolas,monospace', fontSize: 14, lineHeight: `${LINE_HEIGHT}px`, width: '530px', color: '#D4D4D4' }}
               dangerouslySetInnerHTML={{ __html: highlighted }}/>
        </div>
      </div>
    </div>
  );
}
